// client/src/components/parent/TeacherInvitationManager.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const TeacherInvitationManager = ({ token }) => {
  const { childId } = useParams();
  const navigate = useNavigate();

  const [child, setChild] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New invitation form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newInvitation, setNewInvitation] = useState({
    teacherEmail: '',
    teacherName: '',
    schoolName: '',
    gradeLevel: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Confirm dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState(null);

  // Created invitation URL (for copy)
  const [createdInvitation, setCreatedInvitation] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, token]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // Get child info
      const childrenResponse = await api.getLinkedChildren(token);
      const childData = childrenResponse.children?.find(c => c._id === childId);
      if (childData) {
        setChild(childData);
      }

      // Get teacher invitations
      const invitationsResponse = await api.getTeacherInvitations(childId, token);
      setInvitations(invitationsResponse.invitations || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewInvitation(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    setCreatedInvitation(null);

    try {
      const invitationData = {
        ...newInvitation,
        childId,
        assessmentKey: 'vanderbilt-adhd-teacher-rating-scale',
      };

      const response = await api.createTeacherInvitation(invitationData, token);
      // Store the created invitation with URL for sharing
      setCreatedInvitation({
        teacherName: newInvitation.teacherName,
        teacherEmail: newInvitation.teacherEmail,
        portalUrl: response.invitation?.portalUrl,
      });
      setShowNewForm(false);
      setNewInvitation({
        teacherEmail: '',
        teacherName: '',
        schoolName: '',
        gradeLevel: '',
        message: '',
      });
      loadData();
    } catch (err) {
      console.error('Error creating invitation:', err);
      setError(err.message || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess('Link copied!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy link');
    }
  };

  const handleResend = async (invitationId) => {
    try {
      await api.resendTeacherInvitation(invitationId, token);
      setSuccess('Invitation resent successfully!');
      loadData();
    } catch (err) {
      console.error('Error resending invitation:', err);
      setError(err.message || 'Failed to resend invitation');
    }
  };

  const handleDeleteClick = (invitation) => {
    setInvitationToDelete(invitation);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invitationToDelete) return;

    try {
      await api.revokeTeacherInvitation(invitationToDelete._id, token);
      setSuccess('Invitation revoked successfully!');
      setDeleteConfirmOpen(false);
      setInvitationToDelete(null);
      loadData();
    } catch (err) {
      console.error('Error revoking invitation:', err);
      setError(err.message || 'Failed to revoke invitation');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <ScheduleIcon fontSize="small" />, label: 'Pending' },
      sent: { color: 'info', icon: <EmailIcon fontSize="small" />, label: 'Sent' },
      completed: { color: 'success', icon: <CheckIcon fontSize="small" />, label: 'Completed' },
      expired: { color: 'error', label: 'Expired' },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        size="small"
        label={config.label}
        color={config.color}
        icon={config.icon}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/parent/dashboard')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Teacher Invitations for {child?.firstName || 'Child'}
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowNewForm(true)}
          >
            Invite Teacher
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {copySuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setCopySuccess('')}>
          {copySuccess}
        </Alert>
      )}

      {/* Newly Created Invitation - Show URL for Copying */}
      {createdInvitation && createdInvitation.portalUrl && (
        <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'success.main', bgcolor: 'success.lighter' }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CheckIcon color="success" />
            <Typography variant="h6" color="success.main">
              Invitation Created for {createdInvitation.teacherName}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this link with the teacher via email, text, WhatsApp, or any other method.
            They can use this link to complete the assessment.
          </Typography>
          <Box display="flex" gap={1} alignItems="stretch">
            <TextField
              fullWidth
              value={createdInvitation.portalUrl}
              InputProps={{
                readOnly: true,
                startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              size="small"
            />
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={() => handleCopyLink(createdInvitation.portalUrl)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Copy Link
            </Button>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="text"
              size="small"
              onClick={() => setCreatedInvitation(null)}
            >
              Dismiss
            </Button>
          </Box>
        </Paper>
      )}

      {/* New Invitation Form */}
      {showNewForm && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Invite a Teacher
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Send an invitation to your child&apos;s teacher to complete a Vanderbilt ADHD Rating Scale.
            The teacher will receive an email with a secure link to complete the assessment.
          </Typography>

          <form onSubmit={handleCreateInvitation}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="teacherName"
                  label="Teacher's Name"
                  value={newInvitation.teacherName}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="teacherEmail"
                  label="Teacher's Email"
                  type="email"
                  value={newInvitation.teacherEmail}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="schoolName"
                  label="School Name"
                  value={newInvitation.schoolName}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="gradeLevel"
                  label="Grade Level"
                  value={newInvitation.gradeLevel}
                  onChange={handleInputChange}
                  placeholder="e.g., 5th Grade"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="message"
                  label="Personal Message (Optional)"
                  value={newInvitation.message}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Add a personal note to the teacher..."
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button
                variant="outlined"
                onClick={() => setShowNewForm(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </Box>
          </form>
        </Paper>
      )}

      {/* Invitations List */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No teacher invitations yet. Click &quot;Invite Teacher&quot; to send one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invitations.map((invitation) => (
                  <TableRow key={invitation._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {invitation.teacherName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {invitation.teacherEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>{invitation.schoolName || '-'}</TableCell>
                    <TableCell>{invitation.gradeLevel || '-'}</TableCell>
                    <TableCell>{getStatusChip(invitation.status)}</TableCell>
                    <TableCell>{formatDate(invitation.sentAt)}</TableCell>
                    <TableCell>{formatDate(invitation.completedAt)}</TableCell>
                    <TableCell align="right">
                      {invitation.status !== 'completed' && (
                        <>
                          {invitation.portalUrl && (
                            <Tooltip title="Copy Invitation Link">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleCopyLink(invitation.portalUrl)}
                                sx={{ mr: 1 }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Resend Email">
                            <IconButton
                              size="small"
                              onClick={() => handleResend(invitation._id)}
                              sx={{ mr: 1 }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke Invitation">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(invitation)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {invitation.status === 'completed' && (
                        <Chip
                          size="small"
                          label="View Results"
                          color="primary"
                          variant="outlined"
                          onClick={() => {
                            // TODO: Navigate to results view
                            console.log('View results for:', invitation._id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Revoke Invitation?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke the invitation to {invitationToDelete?.teacherName}?
            They will no longer be able to complete the assessment using their link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherInvitationManager;
